import React, { useEffect, useRef, useState } from "react";
import { FaUpload } from "react-icons/fa";
import addChangeMessage from "../../../../controllers/booking/postTicketing/change/addMessage";
import { enqueueSnackbar } from "notistack";
import { useSelector } from "react-redux";
import { canAddChangeRemark } from "../../../../data/flight/order/getChangeAdminGuidance";
import {
  hydratedThread,
  inOrder,
  mergeThread,
  savedOrEcho,
} from "../../../../utils/order/conversationThread";

const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];

/**
 * Same thread as date change / open ticket: the change request's own
 * conversation. Ops and the agent share it — Check messages in the
 * overflow opens the same thread in a modal.
 */
function RenameConfirmationRemarksSection({ data }) {
  const { user } = useSelector((state) => state.user?.userData);
  const [message, setMessage] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [conversation, setConversation] = useState(() =>
    inOrder(hydratedThread(data?.conversation)),
  );
  const fileRef = useRef(null);

  useEffect(() => {
    const thread = hydratedThread(data?.conversation);
    if (!thread.length) return;
    setConversation((prev) => mergeThread(prev, thread));
  }, [data?.conversation]);

  const canReply = canAddChangeRemark(data);

  if (!data?._id) return null;
  if (!canReply && conversation.length === 0 && !data?.remark) return null;

  const handleReply = async () => {
    const text = message.trim();
    if (!text && !imageFile) {
      return enqueueSnackbar("Please enter a message first", {
        variant: "info",
      });
    }
    if (imageFile) {
      if (!IMAGE_TYPES.includes(imageFile.type)) {
        enqueueSnackbar("Only JPEG, PNG, GIF, or WebP images are allowed.", {
          variant: "warning",
        });
        return;
      }
      if (imageFile.size > MAX_IMAGE_BYTES) {
        enqueueSnackbar("Image must be under 2MB.", { variant: "warning" });
        return;
      }
    }

    setLoading(true);
    let payload;
    if (imageFile) {
      const form = new FormData();
      form.append("message", text || " ");
      form.append("image", imageFile);
      payload = form;
    } else {
      payload = { message: text };
    }

    const res = await addChangeMessage(data?._id, payload).finally(() =>
      setLoading(false),
    );
    if (res?.return) {
      const echo = savedOrEcho(res.data, text || " ", user?._id);
      setConversation((prev) => mergeThread(prev, [echo]));
      setMessage("");
      setImageFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } else {
      enqueueSnackbar(
        res?.msg || res?.message || "Something is wrong. Please try again later.",
        { variant: "error" },
      );
    }
  };

  return (
    <div className="w-full bg-white rounded-xl border border-gray-200 p-5 mt-4">
      <span className="block font-semibold text-gray-900 text-base mb-1">
        Messages
      </span>
      <span className="block text-xs text-gray-500 mb-3">
        Same thread as the agent tracker. We do not push notifications — check
        here often.
      </span>

      <div className="text-sm mb-4">
        {conversation.length === 0 ? (
          <span className="block text-gray-500">
            No messages yet. Reply when you need something from the agent.
          </span>
        ) : (
          conversation.map((entry, messageIndex) => (
            <span className="block mb-2" key={entry._id || messageIndex}>
              <span className="font-medium text-gray-900">
                {user?._id === entry?.account ? "You" : "Agent"}:
              </span>{" "}
              <span className="text-gray-700">{entry?.message}</span>
              {entry?.image ? (
                <a
                  href={entry.image}
                  target="_blank"
                  rel="noreferrer"
                  className="block mt-1 text-primary1 font-medium"
                >
                  View image
                </a>
              ) : null}
            </span>
          ))
        )}
      </div>

      {canReply ? (
        <>
          <textarea
            rows="4"
            placeholder="Write a message to the agent…"
            className="w-full bg-gray-100 text-sm rounded-lg p-3 text-gray-700 border border-gray-200 outline-none resize-none focus:border-transparent"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />

          {imageFile ? (
            <div className="mt-2 flex items-center gap-2 text-xs text-gray-600">
              <span className="truncate">{imageFile.name}</span>
              <button
                type="button"
                className="text-red-600 font-medium"
                onClick={() => {
                  setImageFile(null);
                  if (fileRef.current) fileRef.current.value = "";
                }}
              >
                Remove
              </button>
            </div>
          ) : null}

          <div className="flex items-center justify-between mt-4 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={loading}
                className="bg-primary1 disabled:bg-gray-600 text-white text-sm font-medium w-20 flex items-center justify-center h-9 rounded-md"
                onClick={() => handleReply()}
              >
                {loading ? <div className="buttonLoader"></div> : <>Reply</>}
              </button>
              <button
                type="button"
                className="flex items-center gap-2 bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm font-medium px-4 py-2 rounded-md"
                onClick={() => fileRef.current?.click()}
              >
                <FaUpload size={14} />
                Upload
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) setImageFile(file);
                }}
              />
            </div>

            <span className="text-xs text-gray-500">
              Jpeg, png, gif, webp · Size not more than 2M
            </span>
          </div>
        </>
      ) : null}
    </div>
  );
}

export default RenameConfirmationRemarksSection;
